import { useState, useEffect, useMemo, useCallback } from 'react';
import { useBarcodeCartStore, BarcodeCartItem } from '@/stores/barcodeCartStore';
import { mockCompany } from '@/mocks/masters';
import { getData, postData } from '@/services/FetchNodeServices';
import {
  buildLabelInner,
  clampLabelSize,
  labelCss,
  normalizeLayout,
  printLabelSheet,
  rollWidth,
  barcodeHeightMm,
  toBarcodeFormat,
  toLabelData,
  DEFAULT_LAYOUT,
  DEFAULT_FIELD_SIZES,
  LabelLayout,
  LabelFieldSizes,
} from '@/utils/labelPrint';

type LabelSize = '50x25' | '75x37' | '100x50' | 'custom';
type BarcodeFormat = 'EAN13' | 'CODE128' | 'per-item';

interface LabelSettings {
  size: LabelSize;
  customW: number;
  customH: number;
  format: BarcodeFormat;
  fields: {
    companyName: boolean;
    productName: boolean;
    brand: boolean;
    category: boolean;
    mrp: boolean;
    saleRate: boolean;
    hsnCode: boolean;
    articleNo: boolean;
    barcodeImage: boolean;
    barcodeNumber: boolean;
    sizeColor: boolean;
    customText: boolean;
  };
  customText: string;
  layout: LabelLayout;
}

type FieldKey = keyof LabelSettings['fields'];

const SIZE_MAP: Record<LabelSize, { w: number; h: number; label: string }> = {
  '50x25': { w: 50, h: 25, label: '50×25mm Small' },
  '75x37': { w: 75, h: 37, label: '75×37mm Medium' },
  '100x50': { w: 100, h: 50, label: '100×50mm Large' },
  custom: { w: 0, h: 0, label: 'Custom' },
};

const FIELD_LIST: { key: FieldKey; label: string }[] = [
  { key: 'companyName', label: 'Company Name' },
  { key: 'productName', label: 'Product Name' },
  { key: 'brand', label: 'Brand' },
  { key: 'category', label: 'Category' },
  { key: 'mrp', label: 'MRP' },
  { key: 'saleRate', label: 'Sale Rate' },
  { key: 'hsnCode', label: 'HSN Code' },
  { key: 'articleNo', label: 'Article No' },
  { key: 'sizeColor', label: 'Size / Color' },
  { key: 'customText', label: 'Custom Text' },
  { key: 'barcodeImage', label: 'Barcode Image' },
  { key: 'barcodeNumber', label: 'Barcode Number' },
];

const BARCODE_HEIGHT_OPTIONS = [
  { v: 0.3, label: 'Small' },
  { v: 0.4, label: 'Medium' },
  { v: 0.5, label: 'Large' },
];

// Quick Print (page.tsx) reads this key
const STORAGE_KEY = 'barcode_label_settings';
const PX_PER_MM = 96 / 25.4;

const defaultSettings: LabelSettings = {
  size: '75x37',
  customW: 80,
  customH: 40,
  format: 'CODE128',
  fields: {
    companyName: true,
    productName: true,
    brand: true,
    category: false,
    mrp: true,
    saleRate: true,
    hsnCode: false,
    articleNo: false,
    barcodeImage: true,
    barcodeNumber: true,
    sizeColor: false,
    customText: false,
  },
  customText: '',
  layout: DEFAULT_LAYOUT,
};

function getLabelSize(settings: LabelSettings): { w: number; h: number } {
  const raw =
    settings.size === 'custom'
      ? { w: settings.customW, h: settings.customH }
      : SIZE_MAP[settings.size] ?? SIZE_MAP['75x37'];
  return clampLabelSize(raw.w, raw.h);
}

const btnCls = (active: boolean) =>
  `text-xs px-2 py-1.5 rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
    active
      ? 'bg-indigo-600 border-indigo-600 text-white'
      : 'border-slate-200 text-slate-600 hover:border-indigo-300'
  }`;

// ─── Label Preview — same HTML/CSS as print (WYSIWYG), shows a full row ──────
interface LabelPreviewProps {
  settings: LabelSettings;
  item: BarcodeCartItem;
  companyName: string;
  w: number;
  h: number;
}

function LabelPreview({ settings, item, companyName, w, h }: LabelPreviewProps) {
  const fmt = toBarcodeFormat(settings.format);
  const layout = normalizeLayout(settings.layout);

  const inner = useMemo(
    () => buildLabelInner(toLabelData(item, companyName, settings.customText), settings.fields, fmt),
    [item, companyName, settings.customText, settings.fields, fmt],
  );
  const css = labelCss(w, h, { scope: '.lbl-preview', layout });

  const rowW = rollWidth(w, layout);
  const natW = rowW * PX_PER_MM;
  const natH = h * PX_PER_MM;
  const k = Math.min(440 / natW, 240 / natH, 4);

  return (
    <div
      className="mx-auto border-2 border-dashed border-slate-300 bg-white rounded overflow-hidden"
      style={{ width: natW * k + 4, height: natH * k + 4 }}
    >
      <style>{css}</style>
      <div
        className="lbl-preview"
        style={{ width: natW, height: natH, transform: `scale(${k})`, transformOrigin: 'top left' }}
      >
        <div className="l-row">
          {Array.from({ length: layout.columns }).map((_, i) => (
            <div key={i} className="lbl" dangerouslySetInnerHTML={{ __html: inner }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
interface Props {
  onClose: () => void;
}

export default function LabelDesignerModal({ onClose }: Props) {
  const { items, updateQty } = useBarcodeCartStore();
  const [settings, setSettings] = useState<LabelSettings>(defaultSettings);
  const [rememberSettings, setRememberSettings] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [companyName, setCompanyName] = useState<string>(mockCompany.name);

  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  const previewItem: BarcodeCartItem = items[0] ?? {
    itemId: 'preview',
    name: 'Sample Product Name',
    brand: 'Brand',
    category: 'Category',
    barcode: '7612345678901',
    mrp: 1499,
    saleRate: 1200,
    labelQty: 1,
  };

  const totalLabels = items.reduce((s, i) => s + i.labelQty, 0);

  // Load saved label settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setSettingsLoading(true);
        const response = await getData('api/label-settings/get-label-settings');
        if (response?.success && response?.settings) {
          setSettings({
            ...defaultSettings,
            ...response.settings,
            fields: { ...defaultSettings.fields, ...(response.settings.fields || {}) },
            layout: normalizeLayout(response.settings.layout),
          });
        } else {
          try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
              const local = JSON.parse(raw);
              setSettings({
                ...defaultSettings,
                ...local,
                fields: { ...defaultSettings.fields, ...(local.fields || {}) },
                layout: normalizeLayout(local.layout),
              });
            }
          } catch {
            /* ignore */
          }
        }
      } catch (err) {
        console.error('Failed to load label settings:', err);
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Real company name (instead of mock). Falls back silently.
  useEffect(() => {
    let alive = true;
    getData('api/v1/company/get')
      .then((res: any) => {
        const name = res?.data?.name;
        if (alive && name) setCompanyName(name);
      })
      .catch(() => {
        /* keep fallback */
      });
    return () => {
      alive = false;
    };
  }, []);

  const saveSettingsToBackend = useCallback(
    async (settingsToSave: LabelSettings) => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...settingsToSave, companyName }));
      } catch {
        /* ignore */
      }
      try {
        setSaving(true);
        setSaveStatus('idle');
        const response = await postData('api/label-settings/save-label-settings', {
          settings: settingsToSave,
        });
        if (response?.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        } else {
          setSaveStatus('error');
        }
      } catch (err) {
        console.error('Failed to save label settings:', err);
        setSaveStatus('error');
      } finally {
        setSaving(false);
      }
    },
    [companyName],
  );

  const setField = (key: FieldKey, val: boolean) => {
    setSettings((prev) => ({ ...prev, fields: { ...prev.fields, [key]: val } }));
  };

  const setLayout = <K extends keyof LabelLayout>(key: K, val: LabelLayout[K]) => {
    setSettings((prev) => ({ ...prev, layout: { ...normalizeLayout(prev.layout), [key]: val } }));
  };

  const setFieldSize = (key: keyof LabelFieldSizes, val: number) => {
    setSettings((prev) => {
      const L = normalizeLayout(prev.layout);
      return { ...prev, layout: { ...L, sizes: { ...L.sizes, [key]: val } } };
    });
  };

  const { w, h } = getLabelSize(settings);
  const layout = normalizeLayout(settings.layout);
  const rollW = rollWidth(w, layout);
  const barcodeMm = barcodeHeightMm(w, h, layout);
  const maxBarcodeMm = barcodeHeightMm(w, h, { ...layout, barcodeHeightMm: 999 });

  const handlePrint = () => {
    if (items.length === 0 || printing) return;
    setPrinting(true);
    try {
      if (rememberSettings) saveSettingsToBackend(settings);

      const fmt = toBarcodeFormat(settings.format);
      const labels = items.flatMap((item) => {
        const inner = buildLabelInner(
          toLabelData(item, companyName, settings.customText),
          settings.fields,
          fmt,
        );
        return Array.from({ length: Math.max(0, item.labelQty || 0) }, () => inner);
      });

      printLabelSheet(labels, w, h, layout);
    } finally {
      setPrinting(false);
    }
  };

  if (settingsLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
        <div className="bg-white rounded-xl px-10 py-8 flex flex-col items-center gap-3 shadow-xl">
          <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading label settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-indigo-100 rounded-lg text-indigo-600">
              <i className="ri-layout-2-line text-lg" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-800 text-base">Label Designer</h2>
              <p className="text-xs text-slate-400">
                {items.length} items · {totalLabels} total labels
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Modal body */}
        <div className="flex flex-1 overflow-hidden">
          {/* LEFT — Settings */}
          <div className="w-80 shrink-0 border-r border-slate-100 overflow-y-auto p-5 space-y-5">
            {/* Label Size */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Label Size</p>
              <div className="grid grid-cols-2 gap-1.5 mb-2">
                {(['50x25', '75x37', '100x50', 'custom'] as LabelSize[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSettings((p) => ({ ...p, size: s }))}
                    className={`text-xs px-2 py-2 rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                      settings.size === s
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'border-slate-200 text-slate-600 hover:border-indigo-300'
                    }`}
                  >
                    {SIZE_MAP[s].label}
                  </button>
                ))}
              </div>
              {settings.size === 'custom' && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1">
                    <label className="text-xs text-slate-500 block mb-1">Width (mm)</label>
                    <input
                      type="number"
                      min={15}
                      max={200}
                      step={0.5}
                      value={settings.customW}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setSettings((p) => ({ ...p, customW: Number.isNaN(v) ? p.customW : v }));
                      }}
                      className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-indigo-400"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-slate-500 block mb-1">Height (mm)</label>
                    <input
                      type="number"
                      min={10}
                      max={200}
                      step={0.5}
                      value={settings.customH}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setSettings((p) => ({ ...p, customH: Number.isNaN(v) ? p.customH : v }));
                      }}
                      className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-indigo-400"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Fields to Print + per-field font size */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Fields &amp; Font Size</p>
                <button
                  onClick={() => setLayout('sizes', DEFAULT_FIELD_SIZES)}
                  className="text-[11px] text-indigo-600 hover:underline cursor-pointer"
                >
                  Reset sizes
                </button>
              </div>
              <div className="space-y-1">
                {FIELD_LIST.map(({ key, label }) => {
                  const sizeKey = key === 'barcodeImage' ? null : (key as keyof LabelFieldSizes);
                  const checked = settings.fields[key];
                  return (
                    <div key={key} className="flex items-center justify-between gap-2 h-7">
                      <label className="flex items-center gap-2 cursor-pointer group min-w-0">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => setField(key, e.target.checked)}
                          className="w-3.5 h-3.5 accent-indigo-600 cursor-pointer shrink-0"
                        />
                        <span className="text-xs text-slate-600 group-hover:text-slate-800 transition-colors truncate">
                          {label}
                        </span>
                      </label>
                      {sizeKey && checked && (
                        <div className="flex items-center gap-1 shrink-0">
                          <input
                            type="number"
                            min={4}
                            max={60}
                            step={0.5}
                            value={layout.sizes[sizeKey]}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              if (!Number.isNaN(v)) setFieldSize(sizeKey, v);
                            }}
                            className="w-14 border border-slate-200 rounded-md px-1.5 py-0.5 text-xs text-right outline-none focus:border-indigo-400"
                          />
                          <span className="text-[10px] text-slate-400 w-4">px</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {settings.fields.customText && (
                <input
                  type="text"
                  placeholder="Enter custom text..."
                  value={settings.customText}
                  onChange={(e) => setSettings((p) => ({ ...p, customText: e.target.value }))}
                  className="mt-2 w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm outline-none focus:border-indigo-400"
                />
              )}
            </div>

            {/* Design */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Design</p>

              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-slate-500">Barcode height (mm)</label>
                <button
                  onClick={() => setLayout('barcodeHeightMm', null)}
                  className="text-[11px] text-indigo-600 hover:underline cursor-pointer"
                >
                  Auto
                </button>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="number"
                  min={2}
                  max={maxBarcodeMm}
                  step={0.5}
                  value={barcodeMm}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!Number.isNaN(v) && v > 0) setLayout('barcodeHeightMm', v);
                  }}
                  className="w-20 border border-slate-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-indigo-400"
                />
                <span className="text-[11px] text-slate-400">max {maxBarcodeMm} mm</span>
              </div>

              <label className="text-xs text-slate-500 block mb-1">Text alignment</label>
              <div className="grid grid-cols-2 gap-1.5 mb-3">
                <button onClick={() => setLayout('align', 'left')} className={btnCls(layout.align === 'left')}>
                  Left
                </button>
                <button onClick={() => setLayout('align', 'center')} className={btnCls(layout.align === 'center')}>
                  Center
                </button>
              </div>

              <div className="space-y-1.5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={layout.boldName}
                    onChange={(e) => setLayout('boldName', e.target.checked)}
                    className="w-3.5 h-3.5 accent-indigo-600 cursor-pointer"
                  />
                  <span className="text-xs text-slate-600">Bold product name</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={layout.border}
                    onChange={(e) => setLayout('border', e.target.checked)}
                    className="w-3.5 h-3.5 accent-indigo-600 cursor-pointer"
                  />
                  <span className="text-xs text-slate-600">Print border around label</span>
                </label>
              </div>
            </div>

            {/* Roll Layout — labels per row */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Roll Layout</p>
              <label className="text-xs text-slate-500 block mb-1">Labels per row</label>
              <div className="grid grid-cols-4 gap-1.5 mb-2">
                {[1, 2, 3, 4].map((n) => (
                  <button key={n} onClick={() => setLayout('columns', n)} className={btnCls(layout.columns === n)}>
                    {n}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {layout.columns > 1 && (
                  <div className="flex-1">
                    <label className="text-xs text-slate-500 block mb-1">Gap between (mm)</label>
                    <input
                      type="number"
                      min={0}
                      max={20}
                      step={0.5}
                      value={layout.colGap}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!Number.isNaN(v)) setLayout('colGap', v);
                      }}
                      className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-indigo-400"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <label className="text-xs text-slate-500 block mb-1">Side margin (mm)</label>
                  <input
                    type="number"
                    min={0}
                    max={20}
                    step={0.5}
                    value={layout.sideMargin}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!Number.isNaN(v)) setLayout('sideMargin', v);
                    }}
                    className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-indigo-400"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">
                Roll / page size: <span className="font-semibold text-slate-600">{rollW} × {h} mm</span> (printer
                driver mein yahi stock size banana hai)
              </p>
            </div>

            {/* Barcode Format */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Barcode Format</p>
              <div className="space-y-1.5">
                {([{ val: 'CODE128', label: 'Code128 · Alphanumeric' }] as {
                  val: BarcodeFormat;
                  label: string;
                }[]).map(({ val, label }) => (
                  <button
                    key={val}
                    onClick={() => setSettings((p) => ({ ...p, format: val }))}
                    className={`w-full text-left text-xs px-3 py-2 rounded-md border transition-colors cursor-pointer ${
                      settings.format === val
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'border-slate-200 text-slate-600 hover:border-indigo-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Remember Settings */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberSettings}
                onChange={(e) => setRememberSettings(e.target.checked)}
                className="w-3.5 h-3.5 accent-indigo-600 cursor-pointer"
              />
              <span className="text-xs text-slate-600">Remember settings for next time</span>
            </label>

            {/* Manual Save */}
            <button
              onClick={() => saveSettingsToBackend(settings)}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-indigo-200 text-indigo-600 text-xs font-medium hover:bg-indigo-50 transition-colors disabled:opacity-50 cursor-pointer"
            >
              {saving ? (
                <>
                  <div className="w-3 h-3 border border-indigo-600 border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : saveStatus === 'saved' ? (
                <>
                  <i className="ri-check-line text-green-500" />
                  <span className="text-green-600">Saved!</span>
                </>
              ) : saveStatus === 'error' ? (
                <>
                  <i className="ri-error-warning-line text-red-500" />
                  <span className="text-red-500">Save failed</span>
                </>
              ) : (
                <>
                  <i className="ri-save-line" />
                  Save Settings
                </>
              )}
            </button>
          </div>

          {/* RIGHT — Preview + Quantities */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Live Preview</p>
              <div className="flex flex-col items-center gap-3">
                <LabelPreview settings={settings} item={previewItem} companyName={companyName} w={w} h={h} />
                <p className="text-xs text-slate-400">
                  Label: {w}mm × {h}mm · {layout.columns} per row · Roll: {rollW}mm ·{' '}
                  {settings.format === 'EAN13' ? 'EAN-13' : 'Code128'}
                </p>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Label Quantities</p>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500">Item Name</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500">Labels Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {items.map((item) => (
                      <tr key={item.itemId}>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-slate-700 text-sm">{item.name}</div>
                          <div className="text-xs text-slate-400 font-mono">{item.barcode}</div>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <input
                            type="number"
                            min={1}
                            value={item.labelQty}
                            onChange={(e) => updateQty(item.itemId, parseInt(e.target.value) || 1)}
                            className="w-16 text-center border border-slate-200 rounded-md py-1 text-sm outline-none focus:border-indigo-400"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 text-right text-xs text-slate-500">
                  Total labels: <span className="font-semibold text-slate-700">{totalLabels}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handlePrint}
            disabled={printing || items.length === 0}
            className="flex items-center gap-2 px-5 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-printer-line text-sm" />
            </div>
            Print Labels
          </button>
        </div>
      </div>
    </div>
  );
}
